import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Typography, keyframes } from '@mui/material';
import AppIcon from '../common/AppIcon';
import { Wedding } from '../../services/wedding';

interface WelcomeBannerProps {
  wedding: Wedding;
}

// Float directions: up, up-left, up-right, diagonal drift
const floatUp = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0); }
  15% { opacity: 1; transform: translate(0, -6px) scale(1); }
  80% { opacity: 0.5; transform: translate(0, -45px) scale(0.7); }
  100% { opacity: 0; transform: translate(0, -65px) scale(0); }
`;

const floatUpLeft = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0); }
  15% { opacity: 1; transform: translate(-4px, -6px) scale(1); }
  80% { opacity: 0.5; transform: translate(-20px, -40px) scale(0.7); }
  100% { opacity: 0; transform: translate(-30px, -55px) scale(0); }
`;

const floatUpRight = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0); }
  15% { opacity: 1; transform: translate(4px, -6px) scale(1); }
  80% { opacity: 0.5; transform: translate(20px, -40px) scale(0.7); }
  100% { opacity: 0; transform: translate(30px, -55px) scale(0); }
`;

const sparklePulse = keyframes`
  0%, 100% { opacity: 0.2; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1.3); }
`;

const FLOAT_ANIMATIONS = [floatUp, floatUpLeft, floatUpRight];
const SPARKLE_COUNT = 32;

interface Sparkle {
  left: string;
  bottom: string;
  size: number;
  delay: string;
  duration: string;
  animation: ReturnType<typeof keyframes>;
}

const generateSparkles = (): Sparkle[] =>
  Array.from({ length: SPARKLE_COUNT }, () => {
    const left = `${2 + Math.random() * 96}%`;

    const isPulse = Math.random() < 0.25;

    return {
      left,
      bottom: `${Math.random() * 60}%`,
      size: 2 + Math.random() * 3.5,
      delay: `${Math.random() * 2.5}s`,
      duration: `${1.5 + Math.random() * 2}s`,
      animation: isPulse
        ? sparklePulse
        : FLOAT_ANIMATIONS[Math.floor(Math.random() * FLOAT_ANIMATIONS.length)],
    };
  });

type CountdownResult = {
  value: number;
  label: string;
  special?: undefined;
} | {
  special: string;
  value?: undefined;
  label?: undefined;
} | null;

const getCountdown = (dateStr: string): CountdownResult => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weddingDate = new Date(dateStr + 'T00:00:00');
  const diff = weddingDate.getTime() - today.getTime();

  if (diff < 0) return null;
  if (diff === 0) return { special: 'Today is the day!' };

  const totalDays = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (totalDays >= 30) {
    const months = Math.round(totalDays / 30);
    return { value: months, label: months === 1 ? 'month' : 'months' };
  }
  if (totalDays >= 7) {
    const weeks = Math.round(totalDays / 7);
    return { value: weeks, label: weeks === 1 ? 'week' : 'weeks' };
  }
  return { value: totalDays, label: totalDays === 1 ? 'day' : 'days' };
};

const COUNTDOWN_PHRASES = [
  'until your wedding',
  'until the big day',
  'until you say I do',
  'until the celebration',
  'until your happily ever after',
  'until the magic happens',
];

const WelcomeBanner: React.FC<WelcomeBannerProps> = ({ wedding }) => {
  const countdown = wedding.date ? getCountdown(wedding.date) : null;
  const sparkles = useMemo(generateSparkles, []);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseFade, setPhraseFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseFade(false);
      setTimeout(() => {
        setPhraseIndex((i) => (i + 1) % COUNTDOWN_PHRASES.length);
        setPhraseFade(true);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const hasCountdown = countdown && (countdown.special || countdown.value != null);

  return (
    <Card
      sx={{
        mb: 3,
        padding: '0 !important',
        background: 'linear-gradient(135deg, #FBF7F4 0%, #F5EDE7 40%, #F8F0EA 100%)',
        border: '2px solid',
        borderColor: '#C9A96E40',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        '&:hover .sparkle-overlay': {
          opacity: 1,
        },
      }}
    >
      {/* Left: gradient zone with title + details — diagonal clip on desktop */}
      <Box
        sx={{
          flex: { xs: 'none', md: '1 1 0' },
          minWidth: 0,
          background: 'linear-gradient(135deg, #B07A8F 0%, #C9A2B2 60%, #DDBF8E 100%)',
          color: 'white',
          p: { xs: 2, sm: 2.5, md: 3 },
          pr: { md: 6 },
          position: 'relative',
          overflow: 'hidden',
          clipPath: { xs: 'none', md: 'polygon(0 0, 100% 0, calc(100% - 50px) 100%, 0 100%)' },
          // Bokeh circles
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            left: '30%',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          },
        }}
      >
        {/* Sparkle overlay */}
        <Box
          className="sparkle-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.4s ease',
            zIndex: 0,
          }}
        >
          {sparkles.map((s, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: s.left,
                bottom: s.bottom,
                width: s.size,
                height: s.size,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,223,186,0.6) 100%)',
                boxShadow: '0 0 4px 1px rgba(255,255,255,0.4)',
                animation: `${s.animation} ${s.duration} ${s.delay} infinite ease-in-out`,
              }}
            />
          ))}
        </Box>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', fontFamily: "'Carattere', cursive", fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' }, lineHeight: 1.1 }}>
            {wedding.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
            {wedding.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: 0.9 }}>
                <AppIcon name="calendar" sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {new Date(wedding.date + 'T00:00:00').toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </Typography>
              </Box>
            )}
            {wedding.venue && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: 0.9 }}>
                <AppIcon name="location" sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {wedding.venue}
                </Typography>
              </Box>
            )}
            {/* Countdown inline row — mobile only */}
            {hasCountdown && (
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  gap: 0.75,
                  opacity: 0.9,
                  mt: 0.25,
                }}
              >
                <AppIcon name="hourglass" sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {countdown?.special
                    ? countdown.special
                    : `${countdown?.value} ${countdown?.label} to go`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right: countdown on warm textured background — desktop only */}
      {countdown?.special ? (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            px: { md: 4 },
            py: { md: 2 },
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: '2rem',
              fontWeight: 700,
              color: '#B07A8F',
              letterSpacing: '0.03em',
              textAlign: 'center',
            }}
          >
            {countdown.special}
          </Typography>
        </Box>
      ) : countdown?.value != null ? (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center',
            position: 'relative',
            // Subtle decorative ring (desktop only)
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 160,
              height: 160,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: '#C9A96E18',
              display: { xs: 'none', md: 'block' },
            },
          }}
        >
          {/* Countdown column layout (desktop) */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2.5,
              py: 1,
              minWidth: 200,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: '5rem',
                fontWeight: 400,
                lineHeight: 0.85,
                mb: 0.5,
                color: '#B07A8F',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {countdown.value}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#C9A96E',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mt: 0.75,
                position: 'relative',
                zIndex: 1,
              }}
            >
              {countdown.label}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', mt: 1.5, zIndex: 1, display: { xs: 'none', md: 'block' } }}>
            {/* Invisible phrases to establish max width */}
            {COUNTDOWN_PHRASES.map((phrase) => (
              <Typography
                key={phrase}
                aria-hidden
                sx={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '0.6rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  visibility: 'hidden',
                  height: 0,
                  overflow: 'hidden',
                }}
              >
                {phrase}
              </Typography>
            ))}
            {/* Visible phrase */}
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: '0.6rem',
                fontWeight: 500,
                color: 'grey.700',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                textAlign: 'center',
                opacity: phraseFade ? 1 : 0,
                transition: 'opacity 0.4s ease',
              }}
            >
              {COUNTDOWN_PHRASES[phraseIndex]}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Card>
  );
};

export default WelcomeBanner;
